import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ROADMAP_LABEL_WIDTH, TASKS_LABEL_WIDTH } from "./constants";
import {
  GanttActiveFilters,
  GanttAgendaTask,
  GanttChartProps,
  GanttGroup,
  GanttHeaderTicks,
  GanttTaskViewMode,
  GanttZoomLevel,
} from "./models";
import {
  buildHeaderTicks,
  getPixelsPerDay,
  getRoadmapViewRange,
  getTasksViewRange,
  getTimelineWidth,
  getTodayX,
  resolveTaskStatus,
  WINDOW_DAYS,
} from "./utils";

const MS_PER_DAY = 1000 * 60 * 60 * 24;

// ─── Responsive guardrails ────────────────────────────────────────────────────

/** Never let the timeline column shrink below this — below it the grid scrolls horizontally. */
const MIN_TIMELINE_WIDTH = 360;
/** Label column floor + how much of the container it may take on narrow viewports. */
const MIN_LABEL_WIDTH = 180;
const LABEL_WIDTH_RATIO = 0.4;
/** Window for a roadmap zoom with no entry in `WINDOW_DAYS` — only a legacy calendar-unit zoom that
 *  slipped past `LEGACY_ROADMAP_ZOOMS` can land here, and it still needs a defined span. */
const ROADMAP_FALLBACK_WINDOW_DAYS = 365;
/** Tasks (Model A): open with "now" this many days in from the left, leaving room to scroll left
 *  to overdue tasks while showing the upcoming window. */
const SCROLL_LEAD_IN_DAYS = 2;

// ─── Default zoom granularity ─────────────────────────────────────────────────

/** Legacy task zooms (from the pre-window scale) mapped onto the current window options, so a
 *  caller still passing one lands on a rendered segment instead of an unselected control. */
const LEGACY_TASK_ZOOMS: Partial<Record<GanttZoomLevel, GanttZoomLevel>> = {
  week: "1w",
  month: "1m",
  quarter: "3m",
  year: "1y",
};

/** The same for the roadmap (both modes), which moved from calendar units to windows. */
const LEGACY_ROADMAP_ZOOMS: Partial<Record<GanttZoomLevel, GanttZoomLevel>> = {
  week: "3m",
  month: "3m",
  quarter: "6m",
  year: "1y",
};

const resolveDefaultZoom = (props: GanttChartProps): GanttZoomLevel => {
  if (props.variant === "project-roadmap") {
    // Roadmaps span years, so open on a one-year window.
    if (!props.defaultZoom) return "1y";
    return LEGACY_ROADMAP_ZOOMS[props.defaultZoom] ?? props.defaultZoom;
  }
  // tasks — window-based scale (1w…1y), defaulting to a 2-week window (the near-term horizon users
  // think about day-to-day). Normalize any legacy zoom a caller still passes so one of the rendered
  // segments is always selected.
  const requested = props.defaultZoom;
  if (!requested) return "2w";
  return LEGACY_TASK_ZOOMS[requested] ?? requested;
};

// ─── Filter helpers ───────────────────────────────────────────────────────────

const filterGroups = (groups: GanttGroup[], filters: GanttActiveFilters): GanttGroup[] => {
  const search = filters.search?.trim().toLowerCase();
  const assignees = filters.assignee;
  const objects = filters.object;
  const statuses = filters.status;
  if (!assignees?.length && !objects?.length && !statuses?.length && !search) {
    return groups;
  }
  return groups
    .filter((g) => !objects?.length || objects.includes(g.id))
    .map((g) => {
      const groupMatchesSearch = !!search && g.name.toLowerCase().includes(search);
      const tasks = g.tasks.filter((t) => {
        if (assignees?.length && !t.assignees?.some((a) => assignees.includes(a.uuid))) return false;
        if (statuses?.length && !statuses.includes(resolveTaskStatus(t.status))) return false;
        if (search && !groupMatchesSearch && !t.name.toLowerCase().includes(search)) return false;
        return true;
      });
      /*
        The server-supplied totals describe the unfiltered set, so once the chart filters the rows
        itself they would contradict what is rendered ("12" over 3 rows). Drop them and let the
        header count the rows it is actually showing.
      */
      return {
        ...g,
        tasks,
        taskCount: undefined,
        milestoneTaskCount: undefined,
        overdueTaskCount: undefined,
        statusCounts: undefined,
        // Likewise the pager's total: "N remaining" would count rows this filter has just excluded.
        pagedTaskCount: undefined,
      };
    })
    .filter((g) => {
      // When searching, drop groups with no matches (unless the project/deal name itself matched).
      if (search) return g.tasks.length > 0 || g.name.toLowerCase().includes(search);
      // When a status filter is active, drop groups left with no matching tasks so the
      // range/empty states reflect what's actually rendered (rather than empty shells).
      if (statuses?.length) return g.tasks.length > 0;
      return true;
    });
};

// ─── Hook return type ─────────────────────────────────────────────────────────

interface UseGanttChartResult {
  zoom: GanttZoomLevel;
  setZoom: Dispatch<SetStateAction<GanttZoomLevel>>;
  viewMode: GanttTaskViewMode;
  setViewMode: Dispatch<SetStateAction<GanttTaskViewMode>>;
  expandedGroups: Set<string>;
  toggleGroup: (groupId: string) => void;
  activeFilters: GanttActiveFilters;
  onFilterChange: (filters: GanttActiveFilters) => void;
  viewStart: Date;
  viewEnd: Date;
  timelineWidth: number;
  /** Responsive label-column width (shrinks on narrow viewports). */
  labelWidth: number;
  /** Computed pixels-per-day (width-derived for tasks, fixed for roadmap) — used to position bars. */
  pixelsPerDay: number;
  todayX: number;
  headerTicks: GanttHeaderTicks;
  /** Callback ref for the scrollable body — it is measured, so it must be re-read when it remounts. */
  bodyRef: (el: HTMLDivElement | null) => void;
  scrollToToday: () => void;
  filteredGroups: GanttGroup[];
  isLoading: boolean;
}

// Joins group ids into one dependency key. A NUL byte can't occur in a uuid, so the join is reversible.
const GROUP_ID_SEPARATOR = String.fromCharCode(0);

/** Shared empty list, so "no agenda rows" is a stable dependency rather than a new array each render. */
const EMPTY_AGENDA_TASKS: GanttAgendaTask[] = [];

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useGanttChart = (props: GanttChartProps): UseGanttChartResult => {
  const { variant, isLoading } = props;

  // ── Zoom ──────────────────────────────────────────────────────────────────
  const [zoom, setZoom] = useState<GanttZoomLevel>(() => resolveDefaultZoom(props));

  // ── View mode (tasks only) ────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState<GanttTaskViewMode>(() =>
    variant === "tasks" ? (props.defaultView ?? "timeline") : "timeline"
  );

  // ── Expand / collapse (tasks only) ───────────────────────────────────────
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  /*
    A single project opens expanded; several start collapsed so you get an overview before drilling in.

    "A single project" has to mean the whole set, not the loaded page: with the groups paged server-side
    a first page of one, with more behind it, is not the only object there is. It also can't be decided
    on mount, since a chart whose caller loads lazily mounts with no groups at all — so it is settled the
    first time a complete, single-group set is on screen, and once only, or it would keep reopening a
    group the user has deliberately collapsed.
  */
  const hasAutoExpandedRef = useRef(false);
  const soleGroupId =
    variant === "tasks" && props.groups.length === 1 && !props.hasMoreGroups ? props.groups[0].id : undefined;

  useEffect(() => {
    if (hasAutoExpandedRef.current || !soleGroupId) return;

    hasAutoExpandedRef.current = true;
    setExpandedGroups(new Set([soleGroupId]));
  }, [soleGroupId]);

  const toggleGroup = useCallback((groupId: string): void => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  /*
    Tell a lazily loading caller which groups are open. Driven off `expandedGroups` rather than the
    toggle handler so the single group that opens expanded by default is announced too.

    Deliberately *not* deduplicated here: the caller owns the row cache, and it is the caller that
    clears it when its query changes (a new filter, a refresh). A set remembered across those
    reloads would leave a still-expanded group permanently empty, since nothing would ask for its
    rows again. Announcing an already-cached group is harmless — the caller's own cache drops it.
    A new `onGroupExpand` identity therefore re-announces every open group, which is what reloads
    their rows under the new query.
  */
  const onGroupExpand = variant === "tasks" ? props.onGroupExpand : undefined;

  useEffect(() => {
    if (!onGroupExpand) return;

    expandedGroups.forEach((groupId) => onGroupExpand(groupId));
  }, [expandedGroups, onGroupExpand]);

  /*
    Tell the caller which presentation is on screen — on mount as well as on every toggle — so it can
    load what that view needs. The agenda spans every object, which is a different query from the
    timeline's group-by-group rows.
  */
  const onViewModeChange = variant === "tasks" ? props.onViewModeChange : undefined;

  useEffect(() => {
    onViewModeChange?.(viewMode);
  }, [viewMode, onViewModeChange]);

  /*
    A caller that supplies `agendaTasks` has already fetched the flat list, so the agenda needs nothing
    from the groups. Without them, the agenda is derived from `group.tasks` instead, and every group's
    rows have to be asked for — otherwise switching to it shows nothing until each object has been
    opened in the timeline first. Announced under the same contract as an expand (see above), so a
    reload re-asks and the caller's cache drops the groups it already holds.

    Keyed on the ids joined rather than on `props.groups`, whose identity changes every time a page of
    rows lands — that would re-announce every group on each arrival.

    Note `isHostAgenda` asks *who owns the agenda query*, not *is the agenda on screen* — the two are
    separate axes and both are needed here. The view axis is `viewMode === "agenda"`, checked
    alongside it below; a caller can sit on either side of the source axis in either view.
  */
  const isHostAgenda = variant === "tasks" && props.agendaTasks !== undefined;
  const taskGroupIdsKey = variant === "tasks" ? props.groups.map((group) => group.id).join(GROUP_ID_SEPARATOR) : "";

  useEffect(() => {
    if (!onGroupExpand || viewMode !== "agenda" || isHostAgenda) return;

    taskGroupIdsKey
      .split(GROUP_ID_SEPARATOR)
      .filter(Boolean)
      .forEach((groupId) => onGroupExpand(groupId));
  }, [taskGroupIdsKey, viewMode, onGroupExpand, isHostAgenda]);

  // ── Filters (tasks only) ─────────────────────────────────────────────────
  // When uncontrolled, default the assignee filter to the logged-in user so the
  // chart opens on "my tasks". Controlled callers (activeFilters) own the default.
  const [internalFilters, setInternalFilters] = useState<GanttActiveFilters>(() =>
    props.variant === "tasks" && props.currentUserId ? { assignee: [props.currentUserId] } : {}
  );
  const activeFilters =
    variant === "tasks" && props.activeFilters !== undefined ? props.activeFilters : internalFilters;
  const onFilterChange =
    variant === "tasks" && props.onFilterChange !== undefined ? props.onFilterChange : setInternalFilters;

  // ── Scroll ref + measured container width ─────────────────────────────────
  const bodyElementRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  /*
    Measured from a callback ref rather than an effect. The body is only rendered once the chart has
    something to draw: mounting in the loading state (or the empty state) leaves an effect nothing to
    measure, and a `[]`-dependency effect never runs again once the node does appear. The width then
    stayed 0 for the life of the chart, so the density fell back to the minimum-width floor and every
    row stopped short of the body's right edge. A callback ref fires whenever the node changes, which
    is exactly when the measurement has to be retaken.
  */
  const bodyRef = useCallback((el: HTMLDivElement | null): void => {
    bodyElementRef.current = el;
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!el) return;

    // Measure immediately, so the first paint with a body is already at the right density even where
    // ResizeObserver is unavailable; the observer only keeps it right through resizes.
    setContainerWidth(el.clientWidth);
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => setContainerWidth(el.clientWidth));
    observer.observe(el);
    observerRef.current = observer;
  }, []);

  // The ref callback disconnects on every node change; this covers the chart itself going away.
  useEffect(() => () => observerRef.current?.disconnect(), []);

  // Shrink the label column on narrow viewports (never below a readable floor) so the timeline
  // keeps usable space; falls back to the full width until the container has been measured.
  const labelWidth = useMemo(() => {
    const base = variant === "tasks" ? TASKS_LABEL_WIDTH : ROADMAP_LABEL_WIDTH;
    if (!containerWidth) return base;
    return Math.round(Math.min(base, Math.max(MIN_LABEL_WIDTH, containerWidth * LABEL_WIDTH_RATIO)));
  }, [variant, containerWidth]);

  // ── Filtered groups (tasks only) ──────────────────────────────────────────
  // Single source of truth for the visible task set — applies assignee, object,
  // status and search filters. The date range and the rendered rows both derive
  // from this so filtering, range calculation and empty states stay in sync.
  // (`variant === "tasks" ? props.groups : null` is needed because `props` is a
  // discriminated union and `props.groups` only exists on the tasks variant — it
  // narrows the dependency rather than being a performance optimisation.)
  const filteredGroups = useMemo(() => {
    if (variant !== "tasks") return [];
    return filterGroups(props.groups, activeFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, variant === "tasks" ? props.groups : null, activeFilters]);

  // ── Density ─────────────────────────────────────────────────────────────────
  // Available timeline width (viewport minus the sticky label column), floored so the grid never
  // squishes below a readable width. Every window zoom derives its density from this (Model A — the
  // labelled span fills the viewport).
  const available = Math.max(MIN_TIMELINE_WIDTH, containerWidth - labelWidth);
  const pixelsPerDay = useMemo(() => getPixelsPerDay(zoom, available), [zoom, available]);

  /*
    In agenda the caller's flat rows are the rendered set, and the groups hold none — their leaves are
    only loaded for the timeline. So the range is built from those rows, or every bar would fall outside
    the window and the list would show no dates at all.
  */
  const agendaRangeTasks =
    variant === "tasks" && viewMode === "agenda" ? (props.agendaTasks ?? EMPTY_AGENDA_TASKS) : EMPTY_AGENDA_TASKS;

  // ── Date range ────────────────────────────────────────────────────────────
  const { start: viewStart, end: viewEnd } = useMemo(() => {
    if (variant === "tasks") {
      // Model A: a forward-looking window anchored near today. No fill/stretch — the window IS
      // the zoom, so the density above makes the labelled span fill the viewport exactly.
      const windowDays = WINDOW_DAYS[zoom] ?? 30;
      return getTasksViewRange(filteredGroups, windowDays, agendaRangeTasks);
    }
    /*
      Roadmap (both modes): the zoom is a window (3m…20y), so the density above already makes that span
      fill the viewport — the range only has to be long enough to hold it. A roadmap shorter than the
      window is extended to it (otherwise the drawn timeline stops short of the body's right edge); one
      longer than the window keeps its own span and scrolls.
    */
    const base = getRoadmapViewRange(props.rows);
    const windowDays = WINDOW_DAYS[zoom] ?? ROADMAP_FALLBACK_WINDOW_DAYS;
    const windowEnd = new Date(base.start.getTime() + windowDays * MS_PER_DAY);
    return { start: base.start, end: base.end > windowEnd ? base.end : windowEnd };
    // `props.rows` only exists on the roadmap variant; the ternary narrows the discriminated union.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, variant === "project-roadmap" ? props.rows : filteredGroups, agendaRangeTasks, zoom]);

  const timelineWidth = useMemo(
    () => getTimelineWidth(viewStart, viewEnd, pixelsPerDay),
    [viewStart, viewEnd, pixelsPerDay]
  );

  const todayX = useMemo(() => getTodayX(viewStart, pixelsPerDay), [viewStart, pixelsPerDay]);

  const headerTicks: GanttHeaderTicks = useMemo(
    () => buildHeaderTicks(viewStart, viewEnd, zoom, pixelsPerDay),
    [viewStart, viewEnd, zoom, pixelsPerDay]
  );

  /**
   * Anchor the initial scroll. Tasks (Model A): open with "now" near the left edge (a few days of
   * lead-in so overdue tasks are reachable), showing the upcoming window. Roadmap: centre today.
   */
  const scrollToToday = useCallback((): void => {
    const el = bodyElementRef.current;
    if (!el) return;
    const target =
      variant === "tasks" ? todayX - SCROLL_LEAD_IN_DAYS * pixelsPerDay : labelWidth + todayX - el.clientWidth / 2;
    el.scrollLeft = Math.max(0, target);
  }, [variant, todayX, pixelsPerDay, labelWidth]);

  // Scroll to today on mount and when zoom changes
  useEffect(() => {
    scrollToToday();
  }, [scrollToToday]);

  return {
    zoom,
    setZoom,
    viewMode,
    setViewMode,
    expandedGroups,
    toggleGroup,
    activeFilters,
    onFilterChange,
    viewStart,
    viewEnd,
    timelineWidth,
    labelWidth,
    pixelsPerDay,
    todayX,
    headerTicks,
    bodyRef,
    scrollToToday,
    filteredGroups,
    isLoading: isLoading ?? false,
  };
};
